import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase, type ProfileRow } from "@/integrations/supabase/client";
import { sendActivityEmail } from "@/lib/activity-email";
import { setAuthToken } from "./api";
import { fetchBorrowerBundle } from "./data";
import type { Borrower } from "./types";

type Session =
  | { kind: "borrower"; borrower: Borrower; token: string }
  | { kind: "admin"; id: string; name: string; email: string; role: "super" | "manager"; token: string }
  | null;

interface AuthCtx {
  session: Session;
  initializing: boolean;
  signupBorrower: (i: { name: string; email: string; phone: string; password: string }) => Promise<void>;
  loginBorrower: (email: string, password: string) => Promise<"borrower" | "admin">;
  loginAdmin: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  refreshBorrower: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

/** Idle timeout — money product: require re-login after inactivity. */
const SESSION_IDLE_MS = 30 * 60 * 1000;
const LAST_ACTIVITY_KEY = "crediking_last_activity_at";
/** Legacy key from 24h absolute window — cleared on logout. */
const SESSION_STARTED_KEY = "crediking_session_started_at";

function touchActivity() {
  if (typeof window === "undefined") return;
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

function clearActivityStamp() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(SESSION_STARTED_KEY);
}

function isIdleExpired(): boolean {
  if (typeof window === "undefined") return false;
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!raw) return false;
  const last = Number(raw);
  if (!Number.isFinite(last) || last <= 0) return false;
  return Date.now() - last > SESSION_IDLE_MS;
}

async function fetchProfile(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return (data as ProfileRow | null) ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let mounted = true;

    const forceLogout = async () => {
      clearActivityStamp();
      setAuthToken(null);
      try {
        await supabase.auth.signOut();
      } catch {
        /* ignore */
      }
      if (mounted) setSession(null);
    };

    const hydrate = async (accessToken: string | null, userId: string | null) => {
      if (!accessToken || !userId) {
        setAuthToken(null);
        clearActivityStamp();
        if (mounted) setSession(null);
        return;
      }

      if (isIdleExpired()) {
        await forceLogout();
        return;
      }

      // Fresh hydrate with a valid session — ensure activity stamp exists.
      if (typeof window !== "undefined" && !localStorage.getItem(LAST_ACTIVITY_KEY)) {
        touchActivity();
      }

      setAuthToken(accessToken);
      try {
        const profile = await fetchProfile(userId);
        if (!profile) {
          if (mounted) setSession(null);
          return;
        }
        if (profile.role === "admin" || profile.role === "super_admin" || profile.role === "manager") {
          if (mounted) {
            setSession({
              kind: "admin",
              id: profile.id,
              name: profile.full_name,
              email: profile.email,
              role: profile.role === "manager" ? "manager" : "super",
              token: accessToken,
            });
          }
        } else {
          const borrower = await fetchBorrowerBundle(profile.id, profile);
          if (mounted) setSession({ kind: "borrower", borrower, token: accessToken });
        }
      } catch (e) {
        console.error("Failed to load profile", e);
        if (mounted) setSession(null);
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      const s = data.session;
      hydrate(s?.access_token ?? null, s?.user.id ?? null).finally(() => {
        if (mounted) setInitializing(false);
      });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      hydrate(s?.access_token ?? null, s?.user.id ?? null);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Track activity + enforce idle timeout while a session is active.
  useEffect(() => {
    if (!session) return;

    let signingOut = false;
    const checkIdle = () => {
      if (signingOut || !isIdleExpired()) return;
      signingOut = true;
      clearActivityStamp();
      setAuthToken(null);
      void supabase.auth.signOut().finally(() => {
        setSession(null);
        signingOut = false;
      });
    };

    const onActivity = () => {
      if (isIdleExpired()) {
        checkIdle();
        return;
      }
      touchActivity();
    };

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "visibilitychange",
    ];
    for (const ev of events) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    const interval = window.setInterval(checkIdle, 30_000);
    checkIdle();

    return () => {
      for (const ev of events) {
        window.removeEventListener(ev, onActivity);
      }
      window.clearInterval(interval);
    };
  }, [session]);

  const value = useMemo<AuthCtx>(
    () => ({
      session,
      initializing,
      async signupBorrower(input) {
        const { data, error } = await supabase.auth.signUp({
          email: input.email,
          password: input.password,
          options: {
            emailRedirectTo: `${window.location.origin}/borrower`,
            data: { full_name: input.name, phone: input.phone },
          },
        });
        if (error) throw new Error(error.message);
        const user = data.user;
        if (!user) throw new Error("Signup failed — no user returned.");

        // Profile row is created by DB trigger on auth.users (see supabase/sql/profiles_signup_trigger.sql).
        if (data.session) touchActivity();
        try {
          sessionStorage.setItem("crediking_just_signed_up", "1");
        } catch {
          /* private mode */
        }
        // Welcome via Brevo (needs session JWT). If confirm-email is on and no session yet, skip.
        if (data.session && user.id) {
          sendActivityEmail("welcome", user.id);
        }
      },
      async loginBorrower(email, password) {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
        const userId = data.user?.id;
        if (!userId) throw new Error("Login failed.");
        touchActivity();
        const profile = await fetchProfile(userId);
        if (!profile) throw new Error("Profile not found.");
        if (profile.role === "admin" || profile.role === "super_admin" || profile.role === "manager") {
          return "admin";
        }
        return "borrower";
      },
      async loginAdmin(email, password) {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
        const userId = data.user?.id;
        if (!userId) throw new Error("Login failed.");
        const profile = await fetchProfile(userId);
        if (!profile || (profile.role !== "admin" && profile.role !== "super_admin" && profile.role !== "manager")) {
          await supabase.auth.signOut();
          throw new Error("This account does not have admin access.");
        }
        touchActivity();
      },
      async requestPasswordReset(email) {
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw new Error(error.message);
      },
      async updatePassword(password) {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw new Error(error.message);
        touchActivity();
      },
      async refreshBorrower() {
        if (session?.kind !== "borrower") return;
        const profile = await fetchProfile(session.borrower.id);
        if (!profile) return;
        const borrower = await fetchBorrowerBundle(profile.id, profile);
        setSession({ kind: "borrower", borrower, token: session.token });
      },
      async logout() {
        await supabase.auth.signOut();
        clearActivityStamp();
        setAuthToken(null);
        setSession(null);
      },
    }),
    [session, initializing],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
};

export const formatNaira = (n: number) =>
  `₦${Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
